<template>
  <div class="col-12">
    <div class="card mb-5 me-4" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ $t('assignments.addTeacherTitle', { subject: subject.name, className: classData.name }) }}
            </h6>
          </div>
        </div>
      </div>
      
      <div class="card-body px-0 pb-2 pe-3">
        <div class="p-4">
          <div class="card card-plain">
            <div class="card-body">
              <form @submit.prevent="handleSubmit">
                <!-- Teacher -->
                <div class="mb-3">
                  <label class="form-label">{{ $t('assignments.selectTeacher') }}</label>
                  <select
                    class="form-select borderless-input"
                    v-model="form.teacher_id"
                    :class="{ 'is-invalid': errors.teacher_id }"
                    required
                    @change="fetchClassGroups"
                    :disabled="isLoading || teachers.length === 0"
                  >
                    <option value="" disabled>{{ $t('assignments.chooseTeacher') }}</option>
                    <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                      {{ teacher.username }} - {{ teacher.email }}
                    </option>
                    <option v-if="teachers.length === 0" disabled>{{ $t('assignments.noTeachers') }}</option>
                  </select>
                  <div class="invalid-feedback" v-if="errors.teacher_id">
                    {{ errors.teacher_id }}
                  </div>
                </div>

                <!-- Group -->
                <div class="mb-3">
                  <label class="form-label">{{ $t('assignments.selectGroup') }}</label>
                  <select
                    class="form-select borderless-input"
                    v-model="form.group_id"
                    :class="{ 'is-invalid': errors.group_id }"
                    required
                    :disabled="!form.teacher_id || isLoadingGroups"
                  >
                    <option value="" disabled>{{ $t('assignments.chooseGroup') }}</option>
                    <option
                      v-for="g in classGroups"
                      :key="g.group_id"
                      :value="g.group_id"
                    >
                      {{ g.name }}
                    </option>
                  </select>

                  <div class="invalid-feedback" v-if="errors.group_id">
                    {{ errors.group_id }}
                  </div>

                  <div
                    v-if="!isLoadingGroups && form.teacher_id && classGroups.length === 0"
                    class="text-danger small mt-2"
                  >
                    <i class="fas fa-info-circle me-2"></i>
                    {{ $t('assignments.noGroups') }}
                    <router-link 
                      :to="{ name: 'ClassGroups', params: { classId: route.params.classId } }"
                      class="text-primary ms-2"
                    >
                      {{ $t('assignments.goToGroupsPage') }}
                    </router-link>
                  </div>

                  <div v-if="isLoadingGroups" class="text-muted small mt-2">
                    <i class="fas fa-spinner fa-spin me-2"></i>
                    {{ $t('assignments.loadingGroups') }}
                  </div>
                </div>

                <!-- Lesson price — meaningless for a teacher paid a flat salary -->
                <div class="mb-3" v-if="selectedTeacherSalaryType !== 'fixed'">
                  <label class="form-label">{{ $t('assignments.lessonPrice') }}</label>
                  <div class="input-group">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      class="form-control borderless-input"
                      v-model="form.lesson_price"
                      :placeholder="$t('assignments.lessonPricePlaceholder')"
                    />
                    <span class="input-group-text">{{ $t('common.currency') }}</span>
                  </div>
                  <small class="text-muted">{{ $t('assignments.lessonPriceHint') }}</small>
                </div>

                <!-- Actions -->
                <div class="d-flex gap-3 mt-4">
                  <button 
                    type="submit" 
                    class="btn btn-primary btn-sm flex-grow-1"
                    :class="{ 'btn-primary': true, 'disabled': isSubmitting || !form.teacher_id || !form.group_id }"
                    :disabled="isSubmitting || !form.teacher_id || !form.group_id"
                  >                  
                    <span v-if="isSubmitting">
                      <i class="fas fa-spinner fa-spin me-2"></i>
                      {{ $t('assignments.adding') }}
                    </span>
                    <span v-else>
                      <i class="fas fa-save ms-2"></i>
                      {{ $t('assignments.addTeacherButton') }}
                    </span>
                  </button>

                  <button 
                    type="button" 
                    class="btn btn-secondary btn-sm flex-grow-1"
                    @click="goBack"
                    :disabled="isSubmitting"
                  >
                    <i class="fas fa-times ms-2"></i>
                    {{ $t('assignments.cancel') }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import api from '@/api';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const toast = useToast();

const classData = ref({});
const subject = ref({});
const teachers = ref([]);
const classGroups = ref([]);
const isLoading = ref(true);
const isLoadingGroups = ref(false);
const isSubmitting = ref(false);

const form = ref({
  class_id: parseInt(route.params.classId),
  subject_id: parseInt(route.params.subjectId),
  teacher_id: '',
  group_id: '', // this will now be the real group_id from backend
  lesson_price: ''
});

const errors = ref({});

// A teacher paid a flat salary has no per-lesson rate at all — used to hide
// the field above, and to make sure a stale typed-in value doesn't get sent
// silently if the admin picks a salaried teacher after typing one in.
const selectedTeacherSalaryType = computed(() => {
  const teacher = teachers.value.find((t) => t.id === form.value.teacher_id);
  return teacher?.salary_type ?? null;
});

watch(selectedTeacherSalaryType, (type) => {
  if (type === 'fixed') {
    form.value.lesson_price = '';
  }
});

const fetchClassData = async () => {
  try {
    const response = await api.get(`/classes/${route.params.classId}`);
    classData.value = response.data.data || response.data;
  } catch (error) {
    toast.error(t('assignments.fetchClassError'));
    console.error("Error fetching class:", error);
  } finally {
    isLoading.value = false;
  }
};

const fetchSubjectData = async () => {
  try {
    const response = await api.get(`/subjects/${route.params.subjectId}`);
    subject.value = response.data.data || response.data;
  } catch (error) {
    toast.error(t('assignments.fetchSubjectError'));
    console.error("Error fetching subject:", error);
  }
};

const fetchTeachers = async () => {
  try {
    const response = await api.get('/teachers');
    teachers.value = response.data.data || response.data;
  } catch (error) {
    toast.error(t('assignments.fetchTeachersError'));
    console.error("Error fetching teachers:", error);
  }
};

const fetchClassGroups = async () => {
  if (!form.value.teacher_id) {
    classGroups.value = [];
    form.value.group_id = '';
    return;
  }

  isLoadingGroups.value = true;
  classGroups.value = [];

  try {
    const resp = await api.get(`/classes/${route.params.classId}/teachergroups`);
    const payload = resp?.data?.data ?? resp?.data;
    const items = Array.isArray(payload) ? payload : (payload?.data ?? []);

    // Normalize to always have group_id + name
    classGroups.value = items.map(it => {
      // Common pattern: { id: pivot_id, group_id: X, group: { id: X, name: '...' } }
      if (it?.group) {
        return {
          group_id: it.group_id ?? it.group.id,
          name: it.group.name
        };
      }

      // Fallback: direct group row or flat structure
      return {
        group_id: it.group_id ?? it.id,
        name: it.name ?? it.group_name ?? ''
      };
    });

  } catch (error) {
    console.error("Error fetching class groups:", error);
    toast.error(t('assignments.fetchGroupsError'));
    classGroups.value = [];
  } finally {
    isLoadingGroups.value = false;
  }
};

const validateForm = () => {
  errors.value = {};
  let isValid = true;

  if (!form.value.teacher_id) {
    errors.value.teacher_id = t('assignments.teacherRequired');
    isValid = false;
  }

  if (!form.value.group_id) {
    errors.value.group_id = t('assignments.groupRequired');
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async () => {
  if (!validateForm()) return;

  isSubmitting.value = true;
  try {
    const payload = {
      ...form.value,
      lesson_price: form.value.lesson_price === '' || form.value.lesson_price === null
        ? null
        : Number(form.value.lesson_price),
    };
    await api.post('/class-group-teachers', payload);
    toast.success(t('assignments.addTeacherSuccess'));
    router.push({
      name: 'class-subject-teachers',
      params: {
        classId: route.params.classId,
        subjectId: route.params.subjectId
      }
    });
  } catch (error) {
    if (error.response?.status === 422) {
      errors.value = error.response.data.errors;
      toast.error(t('assignments.teacherAlreadyAssigned'));
    } else {
      toast.error(t('assignments.addTeacherError'));
      console.error("Error adding teacher:", error);
    }
  } finally {
    isSubmitting.value = false;
  }
};

const goBack = () => {
  router.push({
    name: 'class-subject-teachers',
    params: {
      classId: route.params.classId,
      subjectId: route.params.subjectId
    }
  });
};

onMounted(() => {
  fetchClassData();
  fetchSubjectData();
  fetchTeachers();
});
</script>
